/* eslint typescript-sort-keys/interface: 0 */
import { createContext } from "@smartsheet/lodestar-utils";
import React, { RefObject, useCallback, useContext, useEffect, useState } from "react";

export interface TabsContextShape {
    /**
     * Whether the panel should be auto focused when switching tabs. See TabsProps for more.
     *
     */
    disableAutoTabFocusWhenSwitching: boolean;

    /**
     * Moves focus to the next tab.
     */
    focusNextTab: () => void;

    /**
     * Moves focus to the previous tab.
     */
    focusPreviousTab: () => void;

    /**
     * Whether the tab selection should be manual. See TabsProps for more.
     */
    manual: boolean;

    /**
     * Called when the selected tab is requested to be changed.
     */
    onChange: (newId: string) => void;

    /**
     * If true, the `TabPanel`s in this `Tabs` component will only render their children when that
     * `TabPanel`'s respective tab is active. This can be a helpful performance optimization if the
     * children of hidden tabs are complex.
     */
    onlyRenderActiveTabPanelChildren: boolean;

    /**
     * Registers a tab with the `Tabs` component. Each tab needs to register itself so it can be
     * hooked up to a panel eventually.
     */
    registerTab: (
        id: string,
        ref: RefObject<HTMLElement>,
        overrideRef?: boolean
    ) => {
        unregister: () => void;
    };

    /**
     * Registers a tab panel with the `Tabs` component. Each tab panel needs to register itself so
     * it can be hooked up to a tab eventually.
     *
     * SCARY NOTE: This should be called AFTER the corresponding Tab is registered. If it is not,
     * the tab panel will not be hooked up to the tab and will not be accessible when selected.
     */
    registerTabPanel: (
        id: string,
        ref: RefObject<HTMLElement>
    ) => {
        unregister: () => void;
    };

    renderOn: "work-surface" | "canvas";

    /**
     * The current selected tab's `id`.
     */
    selectedId: string;

    /**
     * The index of the selected tab.
     */
    selectedIndex: number;

    /**
     * sets the selectedId
     */
    setSelectedId: (selectedId: string) => void;
    /**
     * The array of tabs/panel combos that have registered.
     */
    tabRegistrations: TabRegistration[];
    /**
     * sets the isCollapsed state
     */
    setIsCollapsed: (isCollapsed: boolean) => void;
    /**
     * whether the tabs are in the collapsed state (showing the menu)
     */
    isCollapsed: boolean;
}

// Gonna do something weird here with the context default value. This will NEVER be used since
// We require the Tab, TabList, and TabPanel to be used within Tabs.
const TabsContext = createContext<TabsContextShape>({} as TabsContextShape, "TabsContext");

interface TabRegistration {
    panelId: string | null;
    panelRef: RefObject<HTMLElement> | null;
    tabId: string;
    tabRef: RefObject<HTMLElement> | null;
}

interface TabsContextProviderProps
    extends Pick<
        TabsContextShape,
        | "manual"
        | "onChange"
        | "onlyRenderActiveTabPanelChildren"
        | "renderOn"
        | "selectedId"
        | "disableAutoTabFocusWhenSwitching"
    > {
    children: React.ReactNode;
    areTabsCollapsed?: boolean;
}

export const TabsContextProvider = ({
    children,
    disableAutoTabFocusWhenSwitching,
    manual,
    onChange,
    onlyRenderActiveTabPanelChildren,
    renderOn,
    selectedId,
    areTabsCollapsed = false,
}: TabsContextProviderProps) => {
    const [tabRegistrations, setTabRegistrations] = useState<TabRegistration[]>([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [isCollapsed, setIsCollapsed] = useState(areTabsCollapsed);

    /**
     * @deprecated
     * In V6 this will work differently. TBD on exactly how but it certianly won't return the
     * unregister function. It will likely just mutate the state.No teams outside of
     * lodesatr-core are using this function, and we are no longer calling the unregister function.
     */
    const registerTab = useCallback((tabId: string, ref: RefObject<HTMLElement>, overrideRef?: boolean) => {
        setTabRegistrations((prev) => {
            const exists = prev.find((tab) => tab.tabId === tabId);
            if (exists && !overrideRef) {
                return prev;
            } else if (exists && overrideRef) {
                //keep the rest of the state but just override the ref
                return prev.map((tab) => {
                    if (tab.tabId === tabId) {
                        return {
                            ...tab,
                            tabRef: ref,
                        };
                    } else {
                        return tab;
                    }
                });
            } else {
                return [...prev, { tabId, tabRef: ref, panelId: null, panelRef: null }];
            }
        });
        return {
            unregister: () => {
                setTabRegistrations((prev) => {
                    return prev.filter((tab) => {
                        if (tab.tabId === tabId) {
                            tab.tabRef = null;
                        }
                        // be sure to leave registered panels in the list even if the tab is being
                        //  removed as this may happen when the tab moves into the overflow menu.
                        // Potentially this may leave extra tabs in the list with no tabRef,
                        //  but currently the only way to ensure the panelRef stays around during register/unregister
                        //  race conditions.
                        // For V6, make sure Tabs and TabPanels require a tabId so that can find each other at any time
                        //  instead of relying on this problematic register/unregister system.
                        return tab.tabId !== tabId || tab.panelRef !== null;
                    });
                });
            },
        };
    }, []);
    /**
     * @deprecated
     * In V6 this will work differently. TBD on exactly how but it certianly won't return the
     * unregister function. It will likely just mutate the state. No teams outside of
     * lodesatr-core are using this function, and we are no longer calling the unregister function
     */
    const registerTabPanel: TabsContextShape["registerTabPanel"] = useCallback(
        (panelId: string, ref: RefObject<HTMLElement>) => {
            // Load this panel's reg into the first open slot in the tab registrations.
            setTabRegistrations((prev) => {
                const nextEmptyTabReg = prev.find((tab) => !tab.panelRef);
                if (nextEmptyTabReg) {
                    return prev.map((tab) => {
                        if (tab.tabId === nextEmptyTabReg.tabId) {
                            return {
                                ...tab,
                                panelRef: ref,
                                panelId,
                            };
                        } else {
                            return tab;
                        }
                    });
                }
                return prev;
            });

            return {
                unregister: () => {
                    setTabRegistrations((prev) => {
                        return prev.map((tab) => {
                            if (tab.panelRef?.current && tab.panelRef.current === ref.current) {
                                return {
                                    ...tab,
                                    panelRef: null,
                                };
                            } else {
                                return tab;
                            }
                        });
                    });
                },
            };
        },
        []
    );

    const setSelectedId = useCallback(
        (selectedId: string) => {
            //if selectedId isn't in the tabRegistrations, don't do anything
            if (!tabRegistrations.find((tab) => tab.tabId === selectedId)) return;
            onChange(selectedId);
        },
        [onChange, tabRegistrations]
    );

    useEffect(() => {
        if (tabRegistrations.length === 0) return;
        const foundIndex = tabRegistrations.findIndex((tab) => tab.tabId === selectedId);
        if (foundIndex < 0) {
            let newIndex = 0;
            // try to maintain position if possible
            if (selectedIndex > 0 && selectedIndex - 1 < tabRegistrations.length) newIndex = selectedIndex - 1;
            setSelectedIndex(newIndex);
            setSelectedId(tabRegistrations[newIndex].tabId);
        } else if (foundIndex !== selectedIndex) {
            setSelectedIndex(foundIndex);
        }
    }, [selectedId, tabRegistrations, selectedIndex, setSelectedId]);

    const moveFocusBy = useCallback(
        (amount: -1 | 1) => {
            const selectedIndex = tabRegistrations.findIndex(
                ({ tabRef }) => tabRef?.current === document.activeElement
            );
            setSelectedIndex(selectedIndex);
            const nextIndex = selectedIndex + amount;
            if (nextIndex >= 0 && nextIndex < tabRegistrations.length) {
                if (manual) {
                    tabRegistrations[nextIndex].tabRef?.current?.focus();
                } else {
                    onChange(tabRegistrations[nextIndex].tabId);
                }
            }
        },
        [manual, onChange, tabRegistrations]
    );

    const focusPreviousTab = useCallback(() => {
        moveFocusBy(-1);
    }, [moveFocusBy]);

    const focusNextTab = useCallback(() => {
        moveFocusBy(1);
    }, [moveFocusBy]);

    const contextValue = {
        disableAutoTabFocusWhenSwitching,
        manual,
        onChange,
        onlyRenderActiveTabPanelChildren,
        registerTab,
        registerTabPanel,
        renderOn,
        selectedId,
        selectedIndex,
        focusNextTab,
        focusPreviousTab,
        tabRegistrations,
        setSelectedId,
        isCollapsed,
        setIsCollapsed,
    };
    return <TabsContext.Provider value={contextValue}>{children}</TabsContext.Provider>;
};

export const useTabsContext = () => {
    return useContext(TabsContext);
};
